import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, of } from 'rxjs';
import { BaseRespone } from 'src/app/models/common.response';
import { environment } from 'src/environments/environment';
import { AbhaAddressResult, AbhaAddressSuggestionResult, AbhaAuditAcquisitionRequest, AbhaCardResult, AbhaCareContext, AbhaEnrolmentResult, AbhaEnrolRequest, AbhaFindResult, AbhaFindVerifyRequest, AbhaLoginOtpRequest, AbhaLoginVerifyRequest, AbhaMobileOtpRequest, AbhaMobileVerifyRequest, AbhaOtpResult, AbhaProfileShare, AbhaSelectAccountRequest, AbhaSetAddressRequest } from './iabha-info';

@Injectable({ providedIn: 'root' })
export class AbhaService {

  constructor(private _httpClient: HttpClient) { }

  // Called only after the consent box is ticked, so the consent flag is always true here.
  requestOtp(aadhaarNumber: string, patientId?: number): Observable<BaseRespone<AbhaOtpResult>> {
    const request = { aadhaarNumber, patientId, hasConsented: true };
    return this._httpClient.post<BaseRespone<AbhaOtpResult>>(`${environment.baseURl}/Abha/request-otp`, request).pipe(
      catchError((error) => {
        return of(error.error);
      })
    );
  }

  enrolByAadhaar(request: AbhaEnrolRequest): Observable<BaseRespone<AbhaEnrolmentResult>> {
    return this._httpClient.post<BaseRespone<AbhaEnrolmentResult>>(`${environment.baseURl}/Abha/enrol`, request).pipe(
      catchError((error) => {
        return of(error.error);
      })
    );
  }

  requestLoginOtp(request: AbhaLoginOtpRequest): Observable<BaseRespone<AbhaOtpResult>> {
    return this._httpClient.post<BaseRespone<AbhaOtpResult>>(`${environment.baseURl}/Abha/login-request-otp`, request).pipe(
      catchError((error) => {
        return of(error.error);
      })
    );
  }

  verifyLoginOtp(request: AbhaLoginVerifyRequest): Observable<BaseRespone<AbhaEnrolmentResult>> {
    return this._httpClient.post<BaseRespone<AbhaEnrolmentResult>>(`${environment.baseURl}/Abha/login-verify`, request).pipe(
      catchError((error) => {
        return of(error.error);
      })
    );
  }

  verifyFindOtp(request: AbhaFindVerifyRequest): Observable<BaseRespone<AbhaFindResult>> {
    return this._httpClient.post<BaseRespone<AbhaFindResult>>(`${environment.baseURl}/Abha/find-verify`, request).pipe(
      catchError((error) => {
        return of(error.error);
      })
    );
  }

  selectFoundAccount(request: AbhaSelectAccountRequest): Observable<BaseRespone<AbhaEnrolmentResult>> {
    return this._httpClient.post<BaseRespone<AbhaEnrolmentResult>>(`${environment.baseURl}/Abha/find-select`, request).pipe(
      catchError((error) => {
        return of(error.error);
      })
    );
  }

  requestMobileOtp(request: AbhaMobileOtpRequest): Observable<BaseRespone<AbhaOtpResult>> {
    return this._httpClient.post<BaseRespone<AbhaOtpResult>>(`${environment.baseURl}/Abha/enrol-mobile-request-otp`, request).pipe(
      catchError((error) => {
        return of(error.error);
      })
    );
  }

  verifyMobileOtp(request: AbhaMobileVerifyRequest): Observable<BaseRespone<AbhaOtpResult>> {
    return this._httpClient.post<BaseRespone<AbhaOtpResult>>(`${environment.baseURl}/Abha/enrol-mobile-verify`, request).pipe(
      catchError((error) => {
        return of(error.error);
      })
    );
  }

  getAddressSuggestions(txnId: string): Observable<BaseRespone<AbhaAddressSuggestionResult>> {
    return this._httpClient.post<BaseRespone<AbhaAddressSuggestionResult>>(`${environment.baseURl}/Abha/address-suggestions`, { txnId }).pipe(
      catchError((error) => {
        return of(error.error);
      })
    );
  }

  setAddress(request: AbhaSetAddressRequest): Observable<BaseRespone<AbhaAddressResult>> {
    return this._httpClient.post<BaseRespone<AbhaAddressResult>>(`${environment.baseURl}/Abha/set-address`, request).pipe(
      catchError((error) => {
        return of(error.error);
      })
    );
  }

  getCard(cardToken: string): Observable<BaseRespone<AbhaCardResult>> {
    return this._httpClient.post<BaseRespone<AbhaCardResult>>(`${environment.baseURl}/Abha/card`, { cardToken }).pipe(
      catchError((error) => {
        return of(error.error);
      })
    );
  }

  getProfileShares(): Observable<BaseRespone<AbhaProfileShare[]>> {
    return this._httpClient.get<BaseRespone<AbhaProfileShare[]>>(`${environment.baseURl}/Abha/profile-shares`).pipe(
      catchError((error) => {
        return of(error.error);
      })
    );
  }

  auditAcquisition(request: AbhaAuditAcquisitionRequest): Observable<BaseRespone<boolean>> {
    return this._httpClient.post<BaseRespone<boolean>>(`${environment.baseURl}/Abha/audit-acquisition`, request).pipe(
      catchError((error) => {
        return of(error.error);
      })
    );
  }

  linkCareContext(request: { opdId?: number; ipdId?: number }): Observable<BaseRespone<AbhaCareContext>> {
    return this._httpClient.post<BaseRespone<AbhaCareContext>>(`${environment.baseURl}/Abha/link-care-context`, request).pipe(
      catchError((error) => {
        return of(error.error);
      })
    );
  }

  getCareContexts(request: { opdId?: number; ipdId?: number; patientId?: number }): Observable<BaseRespone<AbhaCareContext[]>> {
    return this._httpClient.get<BaseRespone<AbhaCareContext[]>>(`${environment.baseURl}/Abha/care-contexts`, { params: this.buildParams(request) }).pipe(
      catchError((error) => {
        return of(error.error);
      })
    );
  }

  private buildParams(request: { opdId?: number; ipdId?: number; patientId?: number }): Record<string, string> {
    const params: Record<string, string> = {};
    if (request.opdId) {
      params['opdId'] = String(request.opdId);
    }

    if (request.ipdId) {
      params['ipdId'] = String(request.ipdId);
    }

    if (request.patientId) {
      params['patientId'] = String(request.patientId);
    }

    return params;
  }
}
